import { useRef, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'state'
import useActiveWeb3React, { ActiveWeb3React } from 'hooks/useActiveWeb3React'

import { setAcceptance, setAccessToken as setAccessTokenAction } from '..'
import { AccessToken } from '../models'
import { authenticate, signAuthMsg, refreshToken } from '../auth'

/**
 * internal hook for VvsGotchi authentication module
 * please consider ensureAuth in ./useAuth hook for application features
 */
export const useSignAndAuthenticate = () => {
  const web3Ref = useRef<ActiveWeb3React>()
  web3Ref.current = useActiveWeb3React()
  const setAccessToken = useSetAccessToken()

  return useCallback(async () => {
    const { account: address } = web3Ref.current

    if (!address) throw new Error('Wallet not connected')

    const timestamp = Date.now()
    const signature = await signAuthMsg(web3Ref.current, address, timestamp)
    const authResponse = await authenticate(signature, address, timestamp)

    const { expiresAt } = setAccessToken(address, authResponse)

    return { address, timestamp, signature, authResponse, expiresAt }
  }, [setAccessToken])
}

export const useAcceptance = () => {
  const signAndAuthenticate = useSignAndAuthenticate()
  const { account } = useActiveWeb3React()
  const dispatch = useAppDispatch()
  const acceptance = useAppSelector((state) => state.vvsgotchi.acceptance[account])

  const requireAcceptance = useRef<boolean>()
  requireAcceptance.current = account && !acceptance
  const signToAccept = useCallback(async () => {
    if (!requireAcceptance.current) return

    const authResult = await signAndAuthenticate()
    if (!authResult) return

    const { address, signature, timestamp } = authResult
    dispatch(
      setAcceptance({
        address,
        acceptance: {
          address,
          signature,
          timestamp,
        },
      }),
    )
  }, [signAndAuthenticate, dispatch])

  return [requireAcceptance.current, signToAccept] as const
}

export const useRefreshAuth = () => {
  const setAccessToken = useSetAccessToken()

  return useCallback(
    async (accessToken: AccessToken, account: string) => {
      if (accessToken?.refreshToken) {
        try {
          const refreshedAuth = await refreshToken(accessToken, account)
          setAccessToken(account, refreshedAuth)

          return true
        } catch (err) {
          console.error(err)
        }
      }

      return false
    },
    [setAccessToken],
  )
}

const ACCESS_TOKEN_DEFAULT_VALID_DURATION = 1000 * 60 * 60 * 24 // for expiresAt fallback
export const useSetAccessToken = () => {
  const dispatch = useAppDispatch()

  return useCallback(
    (
      address: string,
      loginResponse: {
        accessToken: string
        refreshToken: string
        expiresAt?: number
      },
    ) => {
      const expiresAt = loginResponse.expiresAt
        ? loginResponse.expiresAt * 1000
        : Date.now() + ACCESS_TOKEN_DEFAULT_VALID_DURATION
      dispatch(
        setAccessTokenAction({
          address,
          accessToken: {
            token: loginResponse.accessToken,
            refreshToken: loginResponse.refreshToken,
            expiresAt,
          },
        }),
      )
      return { address, expiresAt }
    },
    [dispatch],
  )
}
